import { type Client, createClient } from '@zg-rentals/http-client';
import {
  SatelliteConversationFolderTypeV2,
  type SatelliteWebRenterConversationGetLatestConversationsResponseDto,
  SatelliteWebRenterConversationHandler_getLatestConversations,
  SatelliteWebRenterConversationHandler_getLatestConversations_Path,
} from '@zg-rentals/ts-satellite-client';
import { CONVERSATION_LIST_QUERY_TAG } from '@zg-rentals/satellite-inbox/hooks/useInfiniteConversationList';
import { useQuery } from '@zg-rentals/react-query5';
import { useCurrentUser } from './useUserQuery';

const POST_DATA = {
  folder: SatelliteConversationFolderTypeV2.INBOX,
  limit: 20,
} as const;

const QUERY_META = {
  tags: [CONVERSATION_LIST_QUERY_TAG],
  context: { api: 'satellite-api', details: 'getLatestConversations' },
};

const EMPTY_LATEST_CONVERSATIONS = {
  conversations: [],
  hasNextPage: false,
} satisfies SatelliteWebRenterConversationGetLatestConversationsResponseDto;

// The inbox dot is optional UI. Keep request metrics, but do not create an HTTP error report for
// the expected race where HotPads' cached user state outlives the Satellite session.
let unreadBadgeHttpClient: Client | undefined;

function getUnreadBadgeHttpClient(): Client {
  unreadBadgeHttpClient ??= createClient({
    credentials: 'include',
    logging: false,
    monitoring: true,
    prefixUrl: '/rental',
    redirect: 'error',
    timeout: 60 * 1000,
  });

  return unreadBadgeHttpClient;
}

function isUnauthorizedDatarouterError(error: unknown): boolean {
  return (
    error instanceof Error &&
    error.name === 'DatarouterError' &&
    'responseStatus' in error &&
    error.responseStatus === 401
  );
}

async function getUnreadBadgeConversations(): Promise<SatelliteWebRenterConversationGetLatestConversationsResponseDto> {
  try {
    return await SatelliteWebRenterConversationHandler_getLatestConversations(POST_DATA, getUnreadBadgeHttpClient());
  } catch (error) {
    if (isUnauthorizedDatarouterError(error)) {
      return EMPTY_LATEST_CONVERSATIONS;
    }

    throw error;
  }
}

export function useHasUnreadMessages(): boolean {
  const { data: currentUser } = useCurrentUser();

  const { data } = useQuery({
    queryKey: [SatelliteWebRenterConversationHandler_getLatestConversations_Path, POST_DATA],
    queryFn: getUnreadBadgeConversations,
    enabled: currentUser?.loggedIn === true,
    retry: false,
    staleTime: 2 * 60 * 1000,
    meta: QUERY_META,
  });

  return data?.conversations?.some((c) => c.hasUnreadMessage) ?? false;
}
